import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { getAutoUpdatedMap } from 'shared/hooks/getAutoUpdatedMap';
import { getInitialBadgeEnabledState, activeProfileBadgeIDs } from 'features/catalog/badge-state';
import { BadgeModes } from 'types/badge-modes';
import { ContributorIds, getContributorIds } from 'features/catalog/badge-contributors';

const mapWithBadgeEnabled = (
  current: Map<string, boolean>,
  badgeId: string,
  badgesMap: Map<string, Badge>,
  baselineBadgeIDs: readonly string[],
  submittedBadgeIDs: string[],
  email: string
): Map<string, boolean> => {
  let newMap = new Map(current);
  newMap.set(badgeId, true);
  newMap = getAutoUpdatedMap(newMap, badgesMap, { submittedBadgeIDs, email });
  for (const id of baselineBadgeIDs) {
    newMap.set(id, true);
  }
  return getAutoUpdatedMap(newMap, badgesMap, { submittedBadgeIDs, email });
};

interface UseBadgeEditingArgs {
  mode: BadgeModes;
  badgesMap: Map<string, Badge>;
  selectedProfile: Profile | null;
  profileBadgeIDs: string[];
  contributorIndex?: Map<string, ContributorIds>;
  /** Fallback display name for create flow (e.g. email local-part). */
  defaultDisplayName?: string;
  /** Keep this badge enabled for the session (e.g. modal "Claim this badge"). */
  primeClaimBadgeId?: string | null;
}

interface UseBadgeEditingReturn {
  /** Map of badge ID -> whether enabled/checked. */
  enabledBadgesMap: Map<string, boolean>;
  setEnabledBadgesMap: (map: Map<string, boolean>) => void;
  /** Flat array of currently enabled badge IDs. */
  enabledBadges: string[];
  /** Whether the "save/update" action should be disabled (no changes). */
  saveDisabled: boolean;
  setSaveDisabled: (disabled: boolean) => void;
  /** Whether the "update" button should be disabled (no diff from profile). */
  updateDisabled: boolean;
  /** Whether the "create" button should be disabled. */
  createDisabled: boolean;
  /** Current display name for the profile being edited. */
  displayName: string;
  setDisplayName: (name: string) => void;
  /** displayName if set, otherwise defaultDisplayName (create-flow fallback). */
  effectiveDisplayName: string;
  /** Badges the user toggled ON that weren't in the original profile. */
  newlyAddedBadges: Badge[];
  /** Key that increments when leaving edit mode, used to reset child components. */
  leaveEditModeKey: number;
  setLeaveEditModeKey: React.Dispatch<React.SetStateAction<number>>;
  /** Shared handler for toggling a badge in editing mode. */
  handleSharedBadgeToggle: (badge: Badge) => void;
  /** Badges the selected profile submitted ideas for. */
  badgeCreditsSubmissions: Badge[];
  /** Badges the selected profile designed. */
  badgeDesignSubmissions: Badge[];
}

/**
 * Encapsulates all badge-editing state: enabled map, display name,
 * save/update/create disabled flags, and the badge toggle handler.
 */
export const useBadgeEditing = ({
  mode,
  badgesMap,
  selectedProfile,
  profileBadgeIDs,
  contributorIndex,
  defaultDisplayName = '',
  primeClaimBadgeId = null,
}: UseBadgeEditingArgs): UseBadgeEditingReturn => {
  const [draftEnabledBadgesMap, setDraftEnabledBadgesMap] = useState<Map<string, boolean> | null>(null);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [displayName, setDisplayName] = useState(() => selectedProfile?.name ?? '');
  const [leaveEditModeKey, setLeaveEditModeKey] = useState(0);

  const profileBadgeKey = profileBadgeIDs.join(',');
  const baselineBadgeIDs = useMemo(
    () => activeProfileBadgeIDs(profileBadgeIDs),
    // profileBadgeKey encodes the badge IDs and avoids work on equivalent array references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileBadgeKey]
  );
  const baselineBadgeIDSet = useMemo(() => new Set(baselineBadgeIDs), [baselineBadgeIDs]);

  // Synchronous baseline — avoids an empty-map first paint that marks every owned badge as removed.
  const baselineEnabledBadgesMap = useMemo(() => {
    if (mode !== 'editing_profile' || badgesMap.size === 0) {
      return new Map<string, boolean>();
    }
    return getInitialBadgeEnabledState(profileBadgeIDs, badgesMap);
    // profileBadgeIDs read via profileBadgeKey — avoid reset on new [] reference each render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: key encodes badge IDs
  }, [mode, badgesMap, selectedProfile?.email, profileBadgeKey]);

  // Badges submitted (ideas) by the selected profile
  const badgeCreditsSubmissions = useMemo(() => {
    if (selectedProfile == null || !contributorIndex) return [];
    const { creditIds } = getContributorIds(contributorIndex, selectedProfile.email);
    return creditIds.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
  }, [selectedProfile, contributorIndex, badgesMap]);

  // Badges designed by the selected profile
  const badgeDesignSubmissions = useMemo(() => {
    if (selectedProfile == null || !contributorIndex) return [];
    const { designIds } = getContributorIds(contributorIndex, selectedProfile.email);
    return designIds.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
  }, [selectedProfile, contributorIndex, badgesMap]);

  const submittedBadgeIDs = useMemo(
    () => [...new Set([...badgeCreditsSubmissions, ...badgeDesignSubmissions].map((b) => b.id))],
    [badgeCreditsSubmissions, badgeDesignSubmissions]
  );

  const enabledBadgesMap = useMemo(() => {
    const base = draftEnabledBadgesMap ?? baselineEnabledBadgesMap;
    if (!primeClaimBadgeId || base.get(primeClaimBadgeId) || badgesMap.size === 0) return base;
    return mapWithBadgeEnabled(
      base,
      primeClaimBadgeId,
      badgesMap,
      baselineBadgeIDs,
      submittedBadgeIDs,
      selectedProfile?.email ?? ''
    );
  }, [
    draftEnabledBadgesMap,
    baselineEnabledBadgesMap,
    primeClaimBadgeId,
    badgesMap,
    baselineBadgeIDs,
    submittedBadgeIDs,
    selectedProfile?.email,
  ]);

  const enabledBadges = useMemo(
    () => [...enabledBadgesMap.entries()].filter(([, enabled]) => enabled).map(([id]) => id),
    [enabledBadgesMap]
  );

  const setEnabledBadgesMap = useCallback((map: Map<string, boolean>) => {
    setDraftEnabledBadgesMap(map);
  }, []);

  // Sync display name with selected profile
  useEffect(() => {
    if (selectedProfile != null) {
      setDisplayName(selectedProfile.name);
    } else {
      setDisplayName('');
    }
  }, [selectedProfile]);

  // Drop drafts when the profile/catalog baseline changes or we leave edit mode
  useEffect(() => {
    setDraftEnabledBadgesMap(null);
    setSaveDisabled(true);
  }, [baselineEnabledBadgesMap]);

  /** Diff against the edit-session baseline (not raw sheet IDs), so retired/unknown
   * profile IDs never count as pending removals that enable Claim with 0 updates. */
  const hasBadgeEdits = useMemo(() => {
    const baselineOn = new Set([...baselineEnabledBadgesMap.entries()].filter(([, on]) => on).map(([id]) => id));
    if (baselineOn.size !== enabledBadges.length) return true;
    return enabledBadges.some((id) => !baselineOn.has(id));
  }, [baselineEnabledBadgesMap, enabledBadges]);

  const hasNameEdits = selectedProfile != null && displayName.trim() !== selectedProfile.name;

  const updateDisabled = useMemo(() => {
    if (displayName.includes(',')) return true;
    if (selectedProfile != null) {
      return !hasBadgeEdits && !hasNameEdits;
    }
    // Create: name alone is not enough — need at least one badge claim.
    return !hasBadgeEdits;
  }, [displayName, selectedProfile, hasBadgeEdits, hasNameEdits]);

  // Keep leave-edit / unsaved warning in sync when the user reverts all changes.
  useEffect(() => {
    if (mode !== 'editing_profile' || selectedProfile == null) return;
    setSaveDisabled(!hasBadgeEdits && !hasNameEdits);
  }, [mode, selectedProfile, hasBadgeEdits, hasNameEdits]);

  const effectiveDisplayName = displayName.trim() || defaultDisplayName.trim();

  const createDisabled = useMemo(() => {
    return saveDisabled || !effectiveDisplayName || displayName.includes(',') || updateDisabled;
  }, [saveDisabled, effectiveDisplayName, displayName, updateDisabled]);

  const newlyAddedBadges = useMemo(() => {
    return [...enabledBadgesMap.entries()]
      .filter(([id, enabled]) => enabled && !baselineBadgeIDSet.has(id))
      .map(([id]) => badgesMap.get(id)!)
      .filter(Boolean);
  }, [enabledBadgesMap, baselineBadgeIDSet, badgesMap]);

  const handleSharedBadgeToggle = useCallback(
    (badge: Badge) => {
      // Already-claimed badges cannot be unclaimed in edit mode (session claims still can).
      if (enabledBadgesMap.get(badge.id) && baselineBadgeIDSet.has(badge.id)) {
        return;
      }

      setSaveDisabled(false);
      let newMap = new Map(enabledBadgesMap);
      newMap.set(badge.id, !enabledBadgesMap.get(badge.id));
      newMap = getAutoUpdatedMap(newMap, badgesMap, {
        submittedBadgeIDs,
        email: selectedProfile?.email ?? '',
      });

      // Keep originally claimed badges, then re-run cascades so auto-claim milestones
      // (Always Down / Explorer / …) still drop when unclaiming unfinished their deps.
      for (const id of baselineBadgeIDs) {
        newMap.set(id, true);
      }
      newMap = getAutoUpdatedMap(newMap, badgesMap, {
        submittedBadgeIDs,
        email: selectedProfile?.email ?? '',
      });

      setDraftEnabledBadgesMap(newMap);
    },
    [badgesMap, selectedProfile?.email, enabledBadgesMap, submittedBadgeIDs, baselineBadgeIDs, baselineBadgeIDSet]
  );

  return {
    enabledBadgesMap,
    setEnabledBadgesMap,
    enabledBadges,
    saveDisabled,
    setSaveDisabled,
    updateDisabled,
    createDisabled,
    displayName,
    setDisplayName,
    effectiveDisplayName,
    newlyAddedBadges,
    leaveEditModeKey,
    setLeaveEditModeKey,
    handleSharedBadgeToggle,
    badgeCreditsSubmissions,
    badgeDesignSubmissions,
  };
};
